import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    InputAdornment,
    IconButton,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { CloudUpload, LinkedIn, Twitter, YouTube } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import swal from 'sweetalert';
import { useHistory, useParams } from "react-router-dom";

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const ConferenceSpeakerEditForm = (props) => {
    const id = props.match.params.id;
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const eventid = localStorage.getItem('eventid');
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);

    const [formData, setFormData] = useState({
        name_title: 'Mr',
        speaker_name: '',
        email: '',
        phone_number: '',
        profession: '',
        organization: '',
        country: '',
        city: '',
        biography: '',
        spkeakerProfilephoto: null,
        video: null,
        company_name: '',
        company_logo: null,
        website: '',
        linkedin: '',
        twitter: '',
        youtube: '',
        nationality: '',
        passport_number: '',
        passport_expiry: '',
        needs_accommodation: 0,
        needs_invitation_letter: 0,
    });

    const [currentProfilePhoto, setCurrentProfilePhoto] = useState('');
    const [currentCompanyLogo, setCurrentCompanyLogo] = useState('');

    useEffect(() => {
        const fetchSpeakerData = async () => {
            try {
                const response = await axios.get(`/api/speaker/profile-edit/${id}`);
                const data = response.data.speaker;

                setFormData({
                    name_title: data.name_title || 'Mr',
                    speaker_name: data.participant_name || '',
                    email: data.participant_email || '',
                    phone_number: data.participant_phone_number || '',
                    profession: data.participant_profession || '',
                    organization: data.participant_hostpital_organization || '',
                    country: data.participant_country || '',
                    city: data.participant_city || '',
                    biography: data.speaker_details?.biography || '',
                    spkeakerProfilephoto: null,
                    video: null,
                    company_name: data.participant_company || '',
                    company_logo: null,
                    website: '',
                    linkedin: data.speaker_details?.linkedin || '',
                    twitter: data.speaker_details?.twitter || '',
                    youtube: data.speaker_details?.youtube || '',
                    nationality: data.speaker_details?.nationality || '',
                    passport_number: data.speaker_details?.passport_number || '',
                    passport_expiry: data.speaker_details?.passport_expiry || '',
                    needs_accommodation: data.speaker_details?.needs_accommodation || 0,
                    needs_invitation_letter: data.speaker_details?.needs_invitation_letter || 0,
                });

                if (data.image) {
                    setCurrentProfilePhoto(data.image);
                }
                if (data.speaker_details?.company_logo_path) {
                    setCurrentCompanyLogo(data.speaker_details.company_logo_path);
                }

                setInitialDataLoaded(true);
            } catch (error) {
                console.error('Error fetching speaker data:', error);
                swal({
                    title: "Error",
                    text: "Failed to load speaker data. Please try again.",
                    icon: "error",
                    button: "OK",
                });
            }
        };

        fetchSpeakerData();
    }, [id]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: files[0]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        const formDataToSend = new FormData();

        Object.keys(formData).forEach(key => {
            if (key !== 'spkeakerProfilephoto' && key !== 'company_logo' && key !== 'video') {
                formDataToSend.append(key, formData[key]);
            }
        });

        if (formData.spkeakerProfilephoto) {
            formDataToSend.append('spkeakerProfilephoto', formData.spkeakerProfilephoto);
        }
        if (formData.company_logo) {
            formDataToSend.append('company_logo', formData.company_logo);
        }
        if (formData.video) {
            formDataToSend.append('video', formData.video);
        }

        try {
            await axios.get('/sanctum/csrf-cookie');

            const response = await axios.post(
                `/api/conference-speakers-update/${id}/update`,
                formDataToSend,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.status === 200) {
                swal({
                    title: "Update Successful",
                    text: "Speaker information has been updated successfully!",
                    icon: "success",
                    button: "OK",
                });
                history.push(`/admin/conference/speaker-dashbord/${eventid}`);
            } else {
                setErrors(response.data.errors || {});
            }
        } catch (error) {
            console.error('Update error:', error);
            if (error.response) {
                setErrors(error.response.data.errors || {});
            } else {
                setErrors({ general: 'An error occurred. Please try again.' });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!initialDataLoaded) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Typography variant="h6">Loading speaker data...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: isMobile ? 1 : 2,
            pt: isMobile ? 8 : 10
        }}>
            <Paper elevation={3} sx={{
                p: isMobile ? 2 : 4,
                width: '100%',
                maxWidth: '1200px',
                m: isMobile ? 0 : 2
            }}><Button sx={{
                    float: 'right',
                    marginTop: '2%',
                    ":hover": {
                        color: "white"
                    }
                }}
                    color='success'
                    size='small'
                    variant='contained'
                    onClick={() => { history.goBack() }}>
                    Back
                </Button>
                <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom align="center" sx={{
                    fontWeight: 'bold',
                    mb: 3,
                    fontSize: isMobile ? '1.5rem' : '2.125rem'
                }}>
                    Edit Speaker Information
                </Typography>
                

                <form onSubmit={handleSubmit}>
                    {/* Personal Information */}
                    <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
                        Personal Information
                    </Typography>

                    <Grid container spacing={isMobile ? 2 : 3}>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Title</InputLabel>
                                <Select
                                    name="name_title"
                                    value={formData.name_title}
                                    onChange={handleChange}
                                    label="Title"
                                >
                                    <MenuItem value="Mr">Mr</MenuItem>
                                    <MenuItem value="Mrs">Mrs</MenuItem>
                                    <MenuItem value="Dr">Dr</MenuItem>
                                    <MenuItem value="Prof">Prof</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Full Name"
                                name="speaker_name"
                                value={formData.speaker_name}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                name="phone_number"
                                value={formData.phone_number}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Profession"
                                name="profession"
                                value={formData.profession}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Organization"
                                name="organization"
                                value={formData.organization}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Country"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="City"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Biography"
                                name="biography"
                                value={formData.biography}
                                onChange={handleChange}
                                required
                                multiline
                                rows={4}
                            />
                        </Grid>
                    </Grid>

                    {/* Photo and Video Upload */}
                    <Typography variant="h6" sx={{ mt: 4, mb: 2, color: 'primary.main' }}>
                        Media Uploads
                    </Typography>

                    <Grid container spacing={isMobile ? 2 : 3}>
                        <Grid item xs={12} sm={6}>
                            {currentProfilePhoto && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2">Current Photo:</Typography>
                                    <img
                                        src={`${currentProfilePhoto}`}
                                        alt="Speaker"
                                        style={{
                                            maxWidth: '100%',
                                            height: 'auto',
                                            maxHeight: '150px'
                                        }}
                                    />
                                </Box>
                            )}
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={<CloudUpload />}
                                fullWidth
                                sx={{ py: isMobile ? 1.5 : 2 }}
                            >
                                {isMobile ? 'Update Photo' : 'Update Speaker Photo'}
                                <VisuallyHiddenInput
                                    type="file"
                                    name="spkeakerProfilephoto"
                                    onChange={handleFileChange}
                                    accept="image/*"
                                />
                            </Button>
                            {formData.spkeakerProfilephoto && (
                                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                    Selected: {formData.spkeakerProfilephoto.name}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>

                    {/* Company Information */}
                    <Typography variant="h6" sx={{ mt: 4, mb: 2, color: 'primary.main' }}>
                        Company Information
                    </Typography>

                    <Grid container spacing={isMobile ? 2 : 3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Company Name"
                                name="company_name"
                                value={formData.company_name}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            {currentCompanyLogo && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2">Current Logo:</Typography>
                                    <img
                                        src={`${currentCompanyLogo}`}
                                        alt="Company Logo"
                                        style={{
                                            maxWidth: '100%',
                                            height: 'auto',
                                            maxHeight: '150px'
                                        }}
                                    />
                                </Box>
                            )}
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={<CloudUpload />}
                                fullWidth
                                sx={{ py: isMobile ? 1.5 : 2 }}
                            >
                                {isMobile ? 'Update Logo' : 'Update Company Logo'}
                                <VisuallyHiddenInput
                                    type="file"
                                    name="company_logo"
                                    onChange={handleFileChange}
                                    accept="image/*"
                                />
                            </Button>
                            {formData.company_logo && (
                                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                    Selected: {formData.company_logo.name}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Website"
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            https://
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Social Media */}
                    <Typography variant="h6" sx={{ mt: 4, mb: 2, color: 'primary.main' }}>
                        Social Media Profiles
                    </Typography>

                    <Grid container spacing={isMobile ? 2 : 3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="LinkedIn"
                                name="linkedin"
                                value={formData.linkedin}
                                onChange={handleChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LinkedIn color="primary" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Twitter/X"
                                name="twitter"
                                value={formData.twitter}
                                onChange={handleChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Twitter color="primary" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="YouTube"
                                name="youtube"
                                value={formData.youtube}
                                onChange={handleChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <YouTube color="error" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Travel Information */}
                    <Typography variant="h6" sx={{ mt: 4, mb: 2, color: 'primary.main' }}>
                        Travel Information
                    </Typography>

                    <Grid container spacing={isMobile ? 2 : 3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Nationality"
                                name="nationality"
                                value={formData.nationality}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Passport Number"
                                name="passport_number"
                                value={formData.passport_number}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                label="Passport Expiry Date"
                                name="passport_expiry"
                                value={formData.passport_expiry}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Need Accommodation?</InputLabel>
                                <Select
                                    name="needs_accommodation"
                                    value={formData.needs_accommodation}
                                    onChange={handleChange}
                                    label="Need Accommodation?"
                                >
                                    <MenuItem value={1}>Yes</MenuItem>
                                    <MenuItem value={0}>No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Need Invitation Letter?</InputLabel>
                                <Select
                                    name="needs_invitation_letter"
                                    value={formData.needs_invitation_letter}
                                    onChange={handleChange}
                                    label="Need Invitation Letter?"
                                >
                                    <MenuItem value={1}>Yes</MenuItem>
                                    <MenuItem value={0}>No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {/* Submit Button */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 4,
                        gap: 2,
                        flexDirection: isMobile ? 'column' : 'row'
                    }}>
                        <Button
                            variant="outlined"
                            size={isMobile ? 'medium' : 'large'}
                            sx={{
                                px: isMobile ? 2 : 6,
                                py: isMobile ? 1 : 1.5,
                                width: isMobile ? '100%' : 'auto'
                            }}
                            onClick={() => history.goBack()}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            size={isMobile ? 'medium' : 'large'}
                            sx={{
                                px: isMobile ? 2 : 6,
                                py: isMobile ? 1 : 1.5,
                                width: isMobile ? '100%' : 'auto'
                            }}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Updating...' : 'Update Speaker'}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default ConferenceSpeakerEditForm;